/**
 * machine.ts — framework-free pitch-session state machine (GOAL.md, task #1).
 *
 * Pure, deterministic reducer that drives session control. It exists to kill the
 * multi-click race that crashes the app: a command (start/stop/pause/resume)
 * must not fire again while the previous one is still being confirmed by native.
 *
 * Truth model:
 *   - Commands are user intent; they are admitted ONLY when `sessionCan(status)`
 *     allows them. While a transition is pending (starting/stopping/pausing/
 *     resuming) every command is a conflicting/duplicate click → ignored.
 *   - Native push events (`PitchTrackerEvent`) are the source of truth:
 *       session_started → running, session_stopped → idle, error → error.
 *   - `pitch_frame` is the universal liveness signal → it re-syncs an orphaned
 *     session back to running (e.g. idle while frames are still arriving).
 *   - `confirm` is the native promise acknowledgement for the in-flight command.
 *     start/stop also carry a push event, but native pause()/resume() resolve
 *     ONLY their promise (no push event), so the hook feeds `confirm` to release
 *     `pausing`/`resuming`. The timeout fallback (task #2) feeds it too.
 *
 * No React / no native imports — every input is injected, so the machine is
 * trivially unit-testable.
 */
import type { PitchTrackerEvent } from '../types';

export type SessionStatus =
  | 'idle'
  | 'starting'
  | 'running'
  | 'pausing'
  | 'paused'
  | 'resuming'
  | 'stopping'
  | 'error';

export type SessionCommand = 'start' | 'stop' | 'pause' | 'resume';

export interface SessionMachineState {
  status: SessionStatus;
  /** Capture sample rate from the last session_started / pitch_frame, or null. */
  captureSampleRate: number | null;
}

export type SessionInput =
  | { kind: 'command'; command: SessionCommand }
  | { kind: 'event'; event: PitchTrackerEvent }
  | { kind: 'confirm'; command: SessionCommand };

/** Per-command admissibility for the given status (drives can.* / button disable). */
export interface SessionGuards {
  start: boolean;
  stop: boolean;
  pause: boolean;
  resume: boolean;
}

export const initialSessionState: SessionMachineState = {
  status: 'idle',
  captureSampleRate: null,
};

const PENDING_STATUSES: readonly SessionStatus[] = [
  'starting',
  'stopping',
  'pausing',
  'resuming',
];

/** A transition is in flight — all control buttons must be disabled. */
export const isSessionPending = (status: SessionStatus): boolean =>
  PENDING_STATUSES.includes(status);

/**
 * Which commands are admissible. Nothing is admissible while pending, which is
 * exactly what blocks multi-click races. `stop` is allowed from `error` so a
 * wedged session can always be torn down.
 */
export const sessionCan = (status: SessionStatus): SessionGuards => ({
  start: status === 'idle' || status === 'error',
  stop: status === 'running' || status === 'paused' || status === 'error',
  pause: status === 'running',
  resume: status === 'paused',
});

const COMMAND_PENDING: Record<SessionCommand, SessionStatus> = {
  start: 'starting',
  stop: 'stopping',
  pause: 'pausing',
  resume: 'resuming',
};

const reduceCommand = (
  state: SessionMachineState,
  command: SessionCommand,
): SessionMachineState => {
  // Guard: ignore conflicting/duplicate commands (incl. anything while pending).
  if (!sessionCan(state.status)[command]) return state;
  return { ...state, status: COMMAND_PENDING[command] };
};

const reduceConfirm = (
  state: SessionMachineState,
  command: SessionCommand,
): SessionMachineState => {
  // A confirm only settles the matching pending state; stale acks are ignored.
  if (state.status !== COMMAND_PENDING[command]) return state;
  switch (command) {
    case 'start':
    case 'resume':
      return { ...state, status: 'running' };
    case 'pause':
      return { ...state, status: 'paused' };
    case 'stop':
      return { ...initialSessionState };
  }
};

/** Statuses that a stray live signal should revive to `running` (orphan resync). */
const REVIVABLE: readonly SessionStatus[] = [
  'idle',
  'starting',
  'resuming',
  'paused',
  'error',
];

const reduceEvent = (
  state: SessionMachineState,
  event: PitchTrackerEvent,
): SessionMachineState => {
  switch (event.type) {
    case 'session_started':
      // Don't let a late start flip an in-flight stop/pause back to running.
      if (state.status === 'stopping' || state.status === 'pausing')
        return state;
      return {
        status: 'running',
        captureSampleRate: event.session.captureSampleRate,
      };
    case 'session_stopped':
      return { ...initialSessionState };
    case 'error':
      return { ...state, status: 'error' };
    case 'pitch_frame':
      // Frames are truth: a session producing frames is running. Re-sync orphans
      // but never revive a session we are deliberately stopping/pausing.
      if (!REVIVABLE.includes(state.status)) return state;
      return {
        status: 'running',
        captureSampleRate:
          event.frame.captureSampleRate ?? state.captureSampleRate,
      };
    default:
      // input_level, stream_discontinuity, performance_metrics, config_changed,
      // interruption_* — not session-lifecycle, leave state untouched.
      return state;
  }
};

/** Pure, deterministic transition. Same (state, input) → same next state. */
export const sessionReducer = (
  state: SessionMachineState,
  input: SessionInput,
): SessionMachineState => {
  switch (input.kind) {
    case 'command':
      return reduceCommand(state, input.command);
    case 'confirm':
      return reduceConfirm(state, input.command);
    case 'event':
      return reduceEvent(state, input.event);
  }
};
